import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchSiteContent } from "../lib/api";
import { sameOriginUrl } from "../lib/sameOrigin";
import { useApp } from "./AppContext";

const SiteContext = createContext({ site: {}, s: (_, fb) => fb, img: (_, fb) => fb, refresh: () => {} });

export const SiteProvider = ({ children }) => {
  const { lang } = useApp();
  const [site, setSite] = useState({});

  const refresh = useCallback(() => {
    fetchSiteContent().then((d) => setSite(d || {})).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const resolveImageUrl = (u) => {
    if (!u) return u;
    // Strip the Emergent backend host from any stored absolute URL — keeps the
    // browser's inspect element clean and lets assets serve from whatever
    // domain the customer is browsing on.
    return sameOriginUrl(u);
  };

  /** Get translatable string. key suffix _en/_pt is automatic. */
  const s = (key, fallback = "") => {
    const v = site[`${key}_${lang}`] || site[`${key}_en`] || site[key];
    if (v == null || v === "") return fallback;
    return v;
  };

  /** Get a raw (non-translated) value from the CMS. */
  const raw = (key, fallback = "") => {
    const v = site[key];
    if (v == null || v === "") return fallback;
    return v;
  };

  /** Get image URL with fallback (no lang suffix). */
  const img = (key, fallback = "") => {
    const u = site[key];
    if (!u) return fallback;
    return resolveImageUrl(u);
  };

  /** Get csv-string as array. */
  const list = (key, fallback = []) => {
    const v = site[key];
    if (!v || typeof v !== "string") return fallback;
    const parts = v.split(",").map((x) => x.trim()).filter(Boolean);
    return parts.length ? parts : fallback;
  };

  return (
    <SiteContext.Provider value={{ site, s, raw, img, list, refresh }}>{children}</SiteContext.Provider>
  );
};

export const useSite = () => useContext(SiteContext);
